import { createContext, useContext, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

const AdvertiserContext = createContext(null);

export function AdvertiserProvider({ children }) {
  const client = useQueryClient();
  const session = useQuery({
    queryKey: ["auth", "session"],
    queryFn: async () => (await api.get("/auth/session")).data,
    staleTime: 15_000,
    refetchOnMount: "always",
  });
  const me = useQuery({
    queryKey: ["advertiser", "me"],
    queryFn: async () => (await api.get("/advertiser/me")).data,
    enabled: !!session.data?.authenticated,
    staleTime: 10_000,
  });
  const value = useMemo(() => ({
    session: session.data,
    sessionLoading: session.isLoading,
    me: me.data,
    meLoading: me.isLoading,
    meError: me.error,
    isAuthenticated: !!session.data?.authenticated,
    isPending: !!session.data?.pending,
    needs2faSetup: !!session.data?.needs_2fa_setup,
    refetchSession: session.refetch,
    refetchMe: me.refetch,
    invalidate: () => {
      client.invalidateQueries({ queryKey: ["auth", "session"] });
      client.invalidateQueries({ queryKey: ["advertiser"] });
    },
  }), [session.data, session.isLoading, session.refetch, me.data, me.isLoading, me.error, me.refetch, client]);
  return <AdvertiserContext.Provider value={value}>{children}</AdvertiserContext.Provider>;
}

export function useAdvertiser() {
  const ctx = useContext(AdvertiserContext);
  if (!ctx) throw new Error("useAdvertiser must be used inside AdvertiserProvider");
  return ctx;
}
